/**
* RecordCard.js - Componente de UI para mostrar un registro individual.
* CORRECCIÓN: Asegurar inmutabilidad al pasar datos
*/
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getTypeIcon, getCardBorderColor, getExpirationBadge } from '../utils/dataTransformers';

const RecordCard = ({
 record,
 selected,
 onToggleSelect,
 onPlanChange,
 onExtendVigencia,
 onPipelineAdvance,
 onToggleLost,
 onGenerateLinks,
 onEdit,
 loading,
 baseURL,
 compact = true,
 availableUsers = []
}) => {
 const [isExpanded, setIsExpanded] = useState(false);
 
 // ✅ CORRECCIÓN: Debug para verificar que cada card tiene datos únicos
 useEffect(() => {
   return () => {};
 }, [record.id, record.folio, record.nombre]);
 
 // Determinar si estamos en producción o pruebas
 const isProduction = process.env.NODE_ENV === 'production';
 const envPrefix = isProduction ? 'P' : 'U';
 const folioConPrefijo = `${envPrefix}${record.folio}`;
 
 // ✅ CORRECCIÓN: Función para manejar la generación de enlaces con copia del record
 const handleGenerateLinksClick = (e) => {
   e.stopPropagation();
   
   // Crear una copia profunda del record antes de pasarlo
   const recordCopy = {
     ...record,
     // Asegurar que los campos críticos estén copiados
     id: record.id,
     folio: record.folio,
     nombre: record.nombre,
     tipo: record.tipo,
     ownerId: record.ownerId,
     estado: record.estado,
     plan: record.plan
   };
   
   if (onGenerateLinks) {
     onGenerateLinks(recordCopy);
   }
 };
 
 // Método fallback para copiar texto en navegadores que no soportan la API del portapapeles.
 const fallbackCopyTextToClipboard = (text, type) => {
   const textArea = document.createElement("textarea");
   textArea.value = text;
   textArea.style.top = "0";
   textArea.style.left = "0";
   textArea.style.position = "fixed";
   textArea.style.opacity = "0";
   
   document.body.appendChild(textArea);
   textArea.focus();
   textArea.select();
   
   try {
     const successful = document.execCommand('copy');
     if (successful) {
       alert(`🔗 Enlace de ${type} copiado al portapapeles!`);
     } else {
       alert(`Error al copiar el enlace de ${type}`);
     }
   } catch (err) {
     console.error('Fallback: Error al copiar', err);
     alert(`Error al copiar el enlace de ${type}`);
   }
   
   document.body.removeChild(textArea);
 };
 
 // Función para copiar un enlace al portapapeles con feedback visual.
 // eslint-disable-next-line no-unused-vars
 const handleCopyLink = (e, url, type = 'consulta') => {
   e.stopPropagation();
   const btn = e.currentTarget;
   const icon = btn.querySelector('i');
   const originalClass = icon ? icon.className : '';
   
   if (navigator.clipboard && window.isSecureContext) {
     navigator.clipboard.writeText(url).then(() => {
       if (icon) {
         icon.className = 'bi bi-check-circle text-success';
         setTimeout(() => {
           icon.className = originalClass;
         }, 2000);
       } else {
         const originalText = btn.textContent;
         btn.textContent = '✅ Copiado!';
         btn.classList.add('btn-success');
         btn.classList.remove('btn-outline-primary');
         setTimeout(() => {
           btn.textContent = originalText;
           btn.classList.remove('btn-success');
           btn.classList.add('btn-outline-primary');
         }, 2000);
       }
     }).catch(err => {
       console.error('Error al copiar:', err);
       fallbackCopyTextToClipboard(url, type);
     });
   } else {
     fallbackCopyTextToClipboard(url, type);
   }
 };
 
 // Función para generar y descargar un código QR con el folio superpuesto.
// eslint-disable-next-line no-unused-vars
const handleDownloadQR = (e) => {
  e.stopPropagation();
  
  const currentFolio = record.folio;
  const currentId = record.id;
  const currentTipo = record.tipo || 'objeto';
  
  // Para objetos inactivos, usar un nombre descriptivo basado en su estado
  let currentNombre;
  if (record.nombre) {
    currentNombre = record.nombre;
  } else if (record.estado === 'inactivo') {
    currentNombre = `${currentTipo}_inactivo`;
  } else if (record.estado === 'pendientePorLigar') {
    currentNombre = `${currentTipo}_pendiente`;
  } else {
    currentNombre = 'sin_nombre';
  }
  
  const qrUrl = `${baseURL}/consultar?registro=${currentId}`;
  const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrUrl)}&format=png`;
  
  // ✅ Canvas más alto para incluir el folio abajo
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = 300;
  canvas.height = 350; // ⬅️ CAMBIO: Más espacio para folio abajo
  
  const img = new Image();
  img.crossOrigin = 'anonymous';
  
  img.onload = function() {
    // Fondo blanco
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, 300, 350);
    
    // Dibujar el QR en la parte superior
    ctx.drawImage(img, 0, 0, 300, 300);
    
    // ✅ Formatear folio con ceros: P0001, P0042, P9999
    const folioFormateado = currentFolio.toString().padStart(4, '0');
    const folioCompleto = `${envPrefix}${folioFormateado}`;
    
    // ✅ Dibujar folio ABAJO del QR
    ctx.fillStyle = 'black';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(folioCompleto, 150, 325); // ⬅️ Posición abajo del QR
    
    canvas.toBlob(function(blob) {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      
      // Sanitizar el nombre para el archivo
      const nombreArchivo = currentNombre.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
      link.download = `QR_${folioCompleto}_${currentTipo}_${nombreArchivo}.png`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    });
    
    // Feedback visual
    const btn = e.currentTarget;
    const icon = btn.querySelector('i');
    if (icon) {
      icon.className = 'bi bi-check-circle text-success';
      setTimeout(() => {
        icon.className = 'bi bi-qr-code';
      }, 2000);
    }
  };
  
  img.onerror = function() {
    alert('Error al generar el código QR. Intenta de nuevo.');
  };
  
  img.src = qrApiUrl;
};

 // Maneja la expansión/colapso de la tarjeta.
 const toggleExpanded = (e) => {
   if (e.target.type !== 'checkbox' && !e.target.closest('button') && !e.target.closest('select')) {
     setIsExpanded(!isExpanded);
   }
 };

 // Función para renderizar campos específicos según el tipo
const renderTypeSpecificInfo = () => {
  if (record.tipo === 'mascota') {
    return (
      <div className="type-specific-info mt-2 p-2 bg-light rounded">
        <small className="text-muted d-block mb-1"><strong>Información de la mascota:</strong></small>
        {record.raza && <small className="d-block text-muted"><i className="bi bi-tag me-1"></i>Raza: {record.raza}</small>}
        {record.chip && <small className="d-block text-muted"><i className="bi bi-cpu me-1"></i>Chip: {record.chip}</small>}
        {record.especie && <small className="d-block text-muted"><i className="bi bi-heart me-1"></i>Especie: {record.especie}</small>}
        {record.vacunado && <small className="d-block text-muted"><i className="bi bi-check-circle me-1"></i>Vacunado</small>}
        {record.esterilizado && <small className="d-block text-muted"><i className="bi bi-shield-check me-1"></i>Esterilizado</small>}
      </div>
    );
  } else if (record.tipo === 'persona') {
    return (
      <div className="type-specific-info mt-2 p-2 bg-light rounded">
        <small className="text-muted d-block mb-1"><strong>Información de la persona:</strong></small>
        {record.tipoSangrePropietario && <small className="d-block text-muted"><i className="bi bi-droplet me-1"></i>Tipo Sangre: {record.tipoSangrePropietario}</small>}
        {record.personaDeContacto && <small className="d-block text-muted"><i className="bi bi-person me-1"></i>Contacto: {record.personaDeContacto}</small>}
        {record.telefonoDelContacto && <small className="d-block text-muted"><i className="bi bi-telephone me-1"></i>Tel: {record.telefonoDelContacto}</small>}
      </div>
    );
  } else if (record.tipo === 'llavero') {
    return (
      <div className="type-specific-info mt-2 p-2 bg-light rounded">
        <small className="text-muted d-block mb-1"><strong>Información del llavero:</strong></small>
        {record.coleccion && <small className="d-block text-muted"><i className="bi bi-collection me-1"></i>Colección: {record.coleccion}</small>}
        {record.numeroPieza && <small className="d-block text-muted"><i className="bi bi-hash me-1"></i>Pieza: {record.numeroPieza}/{record.totalPiezas || '?'}</small>}
        {record.descripcion && <small className="d-block text-muted"><i className="bi bi-text-left me-1"></i>{record.descripcion}</small>}
      </div>
    );
  }
  return null;
};

 // Devuelve un emoji indicador del estado principal del registro.
 const getStatusIndicator = () => {
   if (record.extraviado) return '🚨';
   if (record.estado === 'activo') return '✅';
   if (record.estado === 'pendientePorLigar') return '🔗';
   if (record.estadoImpresion === 'impreso') return '🖨️';
   return '⏳';
 };

 // --- VISTA COMPACTA ---
 if (compact && !isExpanded) {
   return (
     <div 
       className={`pet-record card mb-1 shadow-sm ${getCardBorderColor(record)} compact-view cursor-pointer`}
       onClick={toggleExpanded}
       style={{ transition: 'all 0.2s ease' }}
     >
       <div className="card-body py-2 px-3">
         <div className="d-flex align-items-center gap-2">
           <input
             type="checkbox"
             className="form-check-input"
             checked={selected}
             onChange={onToggleSelect}
             disabled={loading}
             onClick={(e) => e.stopPropagation()}
           />
           
           <div className="flex-grow-1 d-flex align-items-center gap-2 overflow-hidden">
             <span className="text-nowrap">
               {getTypeIcon(record.tipo)} {record.nombre || 'Sin nombre'}
             </span>
             <span className="badge bg-secondary text-nowrap">
               Folio #{folioConPrefijo}
             </span>
             <span className="text-muted small text-nowrap">
               ({record.tipo})
             </span>
             <span className={`badge ${record.plan === 'premium' ? 'bg-warning' : 'bg-info'} text-nowrap`}>
               {record.plan || 'Básico'}
             </span>
             {record.extraviado && (
               <span className="badge bg-danger text-nowrap">PERDIDO</span>
             )}
             {record.oculto && (
               <span className="badge bg-secondary text-nowrap">
                 <i className="bi bi-eye-slash me-1"></i>Oculto
               </span>
             )}
             {/* Badge de estado de impresión */}
             {record.estadoImpresion === 'impreso' ? (
               <>
                 <span className="badge bg-success text-nowrap">
                   <i className="bi bi-printer me-1"></i>Impreso
                 </span>
                 {record.loteImpresion && (
                   <span className="badge bg-secondary text-nowrap" title="Lote de impresión">
                     <i className="bi bi-box-seam me-1"></i>{record.loteImpresion}
                   </span>
                 )}
               </>
             ) : (
               <span className="badge bg-warning text-dark text-nowrap">
                 <i className="bi bi-hourglass-split me-1"></i>Pend. impr.
               </span>
             )}
           </div>
           
           <div className="d-flex align-items-center gap-2">
             <span style={{ fontSize: '1.2em' }}>
               {getStatusIndicator()}
             </span>
             
             <div className="btn-group" role="group">
               {/* Botón Editar */}
               {onEdit && (
                 <button
                   className="btn btn-sm btn-outline-success"
                   title="Editar registro"
                   onClick={(e) => {
                     e.stopPropagation();
                     onEdit(record);
                   }}
                 >
                   <i className="bi bi-pencil"></i> Editar
                 </button>
               )}
               {/* ✅ CORRECCIÓN: Usar la función que crea copia */}
               <button
                 className="btn btn-sm btn-outline-primary"
                 title="Mostrar enlaces"
                 onClick={handleGenerateLinksClick}
               >
                 <i className="bi bi-link-45deg"></i> Enlaces
               </button>
             </div>
             
             <i className="bi bi-chevron-down text-muted"></i>
           </div>
         </div>
       </div>
     </div>
   );
 }

 // --- VISTA EXPANDIDA ---
 return (
   <div className={`pet-record card mb-2 shadow-sm ${getCardBorderColor(record)} expanded-view`}>
     <div className="card-body p-3">
       {compact && (
         <div className="d-flex justify-content-between align-items-center mb-2">
           <h6 className="mb-0 text-muted">Vista Detallada</h6>
           <button 
             className="btn btn-sm btn-outline-secondary"
             onClick={() => setIsExpanded(false)}
             title="Colapsar"
           >
             <i className="bi bi-chevron-up"></i>
           </button>
         </div>
       )}
       
       <div className="row g-2 align-items-start">
         <div className="col-auto">
           <input
             type="checkbox"
             className="form-check-input mt-2"
             checked={selected}
             onChange={onToggleSelect}
             disabled={loading}
           />
         </div>
         
         <div className="col">
           {/* Header */}
           <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
             <h6 className="mb-0">
               {getTypeIcon(record.tipo)} {record.nombre}
             </h6>
             <span className="badge bg-secondary">
               Folio #{folioConPrefijo}
             </span>
             <span className="text-muted small">({record.tipo})</span>
             {record.extraviado && (
               <span className="badge bg-danger">PERDIDO</span>
             )}
             {(() => {
               const badge = getExpirationBadge(record);
               return badge ? (
                 <span className={`badge ${badge.className}`}>{badge.text}</span>
               ) : null;
             })()}
           </div>
           
           {/* AGREGADO: Campos específicos del tipo */}
           {renderTypeSpecificInfo()}

           {/* Acciones Inline */}
           <div className="inline-actions mt-3 pt-3 border-top">
             <div className="row g-2 align-items-center">
               <div className="col-12 col-sm-auto">
                 <label className="d-flex align-items-center gap-2">
                   <span className="text-nowrap">Plan:</span>
                   <select 
                     className="form-select form-select-sm" 
                     style={{minWidth: '100px'}}
                     value={record.plan || 'light'}
                     onChange={(e) => onPlanChange(record.id, e.target.value)}
                     disabled={loading}
                   >
                     <option value="light">Light</option>
                     <option value="standard">Standard</option>
                     <option value="premium">Premium</option>
                   </select>
                 </label>
               </div>
               
               <div className="col-12 col-sm-auto">
                 <div className="d-flex align-items-center gap-2">
                   <span className="text-nowrap small">
                     Expira: {record.expira}
                   </span>
                   {record.estado === 'activo' && (
                     <button
                       className="btn btn-sm btn-outline-success"
                       onClick={() => onExtendVigencia(record.id, record.plan)}
                       disabled={loading}
                     >
                       +1 Año
                     </button>
                   )}
                 </div>
               </div>

               {record.codigoRascable && (
                 <div className="col-12 col-sm-auto">
                   <span className="text-nowrap small">
                     Código: <code className="bg-light px-1 rounded">{record.codigoRascable}</code>
                   </span>
                 </div>
               )}
               
               <div className="col-auto ms-auto">
                 <div className="btn-group" role="group">
                   {/* ✅ CORRECCIÓN: Usar la función que crea copia */}
                   <button
                     className="btn btn-sm btn-outline-primary"
                     title="Mostrar todos los enlaces"
                     onClick={handleGenerateLinksClick}
                   >
                     <i className="bi bi-link-45deg"></i> Mostrar Enlaces
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       </div>
     </div>
   </div>
 );
};

RecordCard.propTypes = {
 record: PropTypes.object.isRequired,
 selected: PropTypes.bool.isRequired,
 onToggleSelect: PropTypes.func.isRequired,
 onPlanChange: PropTypes.func.isRequired,
 onExtendVigencia: PropTypes.func.isRequired,
 onPipelineAdvance: PropTypes.func.isRequired,
 onToggleLost: PropTypes.func.isRequired,
 onGenerateLinks: PropTypes.func,
 onEdit: PropTypes.func,
 loading: PropTypes.bool,
 baseURL: PropTypes.string.isRequired,
 compact: PropTypes.bool,
 availableUsers: PropTypes.array
};

export default RecordCard;